import { useMemo } from 'react';
import { supabaseCoreClient } from '../supabase.ts';
import { useQuery } from "@supabase-cache-helpers/postgrest-react-query";
import { commonQueryProps } from './cache.js';
import { getFields, useFields, enrichUIConfigWithFields } from './fields.js';
import type { UIComponentMetadata, ProcessedFieldMetadata } from '../../types/metadata.js';
import type { Database } from '../../types/database.types.js';
import { persistedRelations, useMetadataBootReady } from '../persistentMetadataCache.js';

type RawUIComponent = Database['core_crm']['Tables']['table_ui_component']['Row'] & {
  table_details: { table_name: string; schema_name: string };
};

/**
 * Builds the Supabase query for fetching UI component metadata
 * @returns PostgrestBuilder query for table_ui_component with all necessary columns
 */
export const buildQueryForUIComponents = () => {
  return supabaseCoreClient
  .from('table_ui_component')
    .select('id,name,table_info,table_details:table_info!inner(table_name,schema_name),is_primary,ui_data,component_type,created_at,updated_at,created_by,updated_by');
};

/**
 * Post-processes UI component data by enriching with field metadata
 * @param uiComponents - Raw UI component data from database
 * @param fieldData - Processed field metadata for enrichment
 * @returns Enriched UI component metadata
 */
export const postProcessUIComponents = (
  uiComponents: RawUIComponent[], 
  fieldData: ProcessedFieldMetadata
): UIComponentMetadata[] => {
  return uiComponents.flatMap(uiComponent => {
    if (uiComponent.table_info === null) return [];
    const uiData = uiComponent.ui_data && typeof uiComponent.ui_data === 'object' && !Array.isArray(uiComponent.ui_data)
      ? uiComponent.ui_data
      : {};
    const uiComponentEnriched: UIComponentMetadata = {
      ...uiComponent,
      component_name: uiComponent.name,
      table_info: uiComponent.table_info,
      table_name: uiComponent.table_details.table_name,
      schema_name: uiComponent.table_details.schema_name,
      ui_data: { ...uiData, schema_name: uiComponent.table_details.schema_name },
    };
    
    // Define a filter function for cache transition safety
    const cacheTransitionFilter = (_field: any, tableName: string, fieldData: ProcessedFieldMetadata): boolean => {
      // Filter out fields for tables that don't exist in fieldData during cache transitions
      return Object.keys(fieldData).includes(tableName);
    };
    
    // Use the generic enrichment function to handle all nested field arrays AND filtering
    uiComponentEnriched.ui_data = enrichUIConfigWithFields(
      uiComponentEnriched.ui_data,
      fieldData,
      uiComponentEnriched.table_name,
      { fieldFilter: cacheTransitionFilter }
    );

    return [uiComponentEnriched];
  });
};

// Result type for UI component operations
interface UIComponentResult {
  data: UIComponentMetadata[] | null;
  error: any;
}

/**
 * Fetches UI component data outside of React context
 * @returns Promise with processed UI component metadata or error
 */
export const getUIComponents = async (): Promise<UIComponentResult> => {
  const { data: fieldData, error: fieldError } = await getFields();

  if (fieldError || !fieldData) {
    return { data: null, error: fieldError ?? new Error('Field metadata unavailable') };
  }

  const uiComponentQuery = buildQueryForUIComponents();
  const { data: uiComponents, error: uiComponentError } = await uiComponentQuery;

  if (uiComponentError) {
    return { data: null, error: uiComponentError };
  }
  
  const processedData = postProcessUIComponents(uiComponents, fieldData);
  return { data: processedData, error: null };
};

// Hook options interface
interface UseUIComponentsOptions {
  enabled?: boolean;
}

// Hook result interface
interface UseUIComponentsResult {
  data: UIComponentMetadata[] | null;
  error: any;
  isLoading: boolean;
}

/**
 * React hook to fetch UI component data using React Query
 * @param options - Options including enabled flag
 * @returns UI component data with loading and error states
 */
export const useUIComponents = ({ enabled }: UseUIComponentsOptions): UseUIComponentsResult => {
  const bootReady = useMetadataBootReady();
  const { data: fieldData, error: fieldError, isLoading: fieldIsLoading } = useFields({ enabled });

  const uiComponentQuery = buildQueryForUIComponents();
  const {
    data: uiComponents,
    error: uiComponentError,
    isLoading: uiComponentIsLoading
  } = useQuery(
    uiComponentQuery,
    {
      ...commonQueryProps,
      // Only post-processing needs fields; fetch alongside them.
      enabled: enabled && bootReady,
      meta: persistedRelations('core_crm.table_ui_component')
    }
  );

  // Memoized on the source query results so the processed object keeps a stable
  // identity across renders — consumers memoize off it.
  const processedData = useMemo(
    () => (uiComponents && fieldData ? postProcessUIComponents(uiComponents, fieldData) : null),
    [uiComponents, fieldData]
  );

  if (fieldIsLoading || uiComponentIsLoading) {
    return {
      data: null,
      error: fieldError || uiComponentError,
      isLoading: true
    };
  }

  if (fieldError || uiComponentError || !fieldData || !uiComponents) {
    return {
      data: null,
      error: fieldError || uiComponentError,
      isLoading: false
    };
  }

  return {
    data: processedData,
    error: null,
    isLoading: false
  };
};
